import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DragAndDropUpload from "../../../../DragAndDropUpload/DragAndDropUpload";
import DocumentsAPI, {
  Document,
} from "../../../../../../store/reducers/builderReducers/documents/documentsApi";
import { useAppSelector } from "../../../../../../store/hooks";

type PickerView = "upload" | "directory" | "link";

export default function DocumentLinkPicker({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (item: Document) => void;
}) {
  const [view, setView] = useState<PickerView>("upload");
  const [preview, setPreview] = useState<Document | null>(null);
  const [activeNavId, setActiveNavId] = useState<number | null>(null);
  const [link, setLink] = useState("");
  const [filename, setFilename] = useState("");
  const website = useAppSelector((state) => state.builder.website);
  const docStruc = useAppSelector((state) => state.builder.documents);

  const documents = useMemo(
    () => (docStruc?.toArray ? docStruc.toArray() : []),
    [docStruc]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    DocumentsAPI.loadDocumentsTree();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setView("upload");
      setPreview(null);
      setActiveNavId(null);
      setLink("");
      setFilename("");
    }
  }, [open]);

  function findFolderRecursive(
    structure: Document[],
    id: number | null
  ): Document[] | null {
    if (!id) return structure;

    const folders = structure.filter((item) => item.type === "directory");
    for (let i = 0; i < folders.length; i++) {
      if (folders[i].id === id) {
        return folders[i].childElements;
      }

      const item = findFolderRecursive(folders[i].childElements, id);
      if (item) {
        return item;
      }
    }

    return null;
  }

  function getFolderNameRecursive(
    structure: Document[],
    id: number | null,
    breadcrumb: Array<{ name: string; id?: number }> = [
      { name: website?.name || "Files" },
    ]
  ): Array<{ name: string; id?: number }> | null {
    if (!id) return breadcrumb;

    const folders = structure.filter((item) => item.type === "directory");
    for (let i = 0; i < folders.length; i++) {
      if (folders[i].id === id) {
        return [...breadcrumb, { id: folders[i].id, name: folders[i].originalname }];
      }

      if (folders[i].childElements.length) {
        const nested = getFolderNameRecursive(folders[i].childElements, id, [
          ...breadcrumb,
          { id: folders[i].id, name: folders[i].originalname },
        ]);
        if (nested) return nested;
      }
    }

    return null;
  }

  function handleItemClick(item: Document) {
    if (item.type === "directory") {
      setActiveNavId(item.id);
      return;
    }

    setPreview(item);
  }

  function uploadFile(event: any) {
    event.preventDefault();
    event.stopPropagation();
    const files = event?.target?.files ?? event?.dataTransfer?.files;
    if (!files || !files.length) {
      return;
    }

    DocumentsAPI.uploadDocuments(files).then((data) => {
      if (data.addedCustomFiles.length) {
        setPreview(data.addedCustomFiles[0]);
      }
    });
  }

  function uploadFileFromUrl() {
    if (!link) {
      return;
    }

    DocumentsAPI.addReferenceFile({
      path: link,
      originalname: filename || link,
    }).then((data) => {
      if (data.addedCustomFiles.length) {
        setPreview(data.addedCustomFiles[0]);
      }
    });
  }

  function handleBackdropPointerDown(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="modal document-link-picker-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999999999999999999,
        backgroundColor: "rgba(0,0,0,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onMouseDown={handleBackdropPointerDown}
    >
      <div
        className="document-link-picker-panel file-explorer"
        style={{
          position: "relative",
          width: "350px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: "#272727",
          borderRadius: "6px",
          padding: "15px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Select file"
      >
        <div className="b-row">
          <div className="col-12 b-mb-2 d-flex justify-content-between align-items-center">
            <div className="b-fs-5">Select file</div>
            <button
              type="button"
              className="b-btn btn-primary"
              style={{ background: "#444", border: "none", padding: "4px 10px" }}
              onClick={onClose}
            >
              Close
            </button>
          </div>

          {!preview && (
            <div className="col-12 b-mb-3 file-locations">
              <div
                className={
                  "file-locations file-locations__item " +
                  (view === "upload" ? "file-locations__item--active" : "")
                }
                onClick={() => setView("upload")}
              >
                + New file
              </div>
              <div
                className={
                  "file-locations file-locations__item " +
                  (view === "directory" ? "file-locations__item--active" : "")
                }
                onClick={() => setView("directory")}
              >
                Select from folder
              </div>
              <div
                className={
                  "file-locations file-locations__item " +
                  (view === "link" ? "file-locations__item--active" : "")
                }
                onClick={() => setView("link")}
              >
                Link to file
              </div>
            </div>
          )}

          {!preview && view === "upload" && (
            <div className="col-12 b-mb-3">
              <DragAndDropUpload icon="cloud" onChange={uploadFile} />
            </div>
          )}

          {!preview && view === "link" && (
            <>
              <div className="col-12 b-mb-2">
                <div className="fs-small">Link to file</div>
                <input
                  className="form-input builder-input"
                  value={link}
                  onChange={(event) => setLink(event.target.value)}
                />
              </div>
              {link !== "" && (
                <div className="col-12 b-mb-2">
                  <div className="fs-small">Name</div>
                  <input
                    className="form-input builder-input"
                    value={filename}
                    onChange={(event) => setFilename(event.target.value)}
                  />
                </div>
              )}
              <div className="col-12">
                <button
                  type="button"
                  className="b-btn btn-primary w-100"
                  style={{ background: "#444", border: "none" }}
                  onClick={uploadFileFromUrl}
                  disabled={link === ""}
                >
                  Save
                </button>
              </div>
            </>
          )}

          {!preview && view === "directory" && (
            <>
              <div className="col-12 b-mb-2 b-fs-5">
                {getFolderNameRecursive(documents, activeNavId)?.map((item, index) => (
                  <React.Fragment key={`${item.name}-${item.id ?? "root"}`}>
                    {index !== 0 && " > "}
                    <span
                      className="file-explorer__breadcrumb"
                      onClick={() => setActiveNavId(item.id ?? null)}
                    >
                      {item.name}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <div className="col-12 file-explorer__items">
                {findFolderRecursive(documents, activeNavId)
                  ?.sort((a, b) =>
                    a.originalname.toLocaleLowerCase() > b.originalname.toLocaleLowerCase()
                      ? 1
                      : -1
                  )
                  .map((item) => (
                    <div
                      key={item.id}
                      className="col-12 b-py-2 d-flex file-explorer__items__item"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="file-explorer__items__item__icon">
                        {item.type === "directory" ? (
                          <FolderIcon />
                        ) : (
                          <InsertDriveFileOutlinedIcon />
                        )}
                      </div>
                      <div className="file-explorer__items__item__name">
                        {item.originalname}
                      </div>
                    </div>
                  ))}
              </div>
              <div className="col-12 b-pt-3">
                <button
                  type="button"
                  className="b-btn btn-primary w-100"
                  style={{ background: "#444", border: "none" }}
                  onClick={onClose}
                >
                  Back
                </button>
              </div>
            </>
          )}

          {preview && (
            <>
              <div className="col-12 b-mb-2">
                <div
                  style={{
                    width: "100%",
                    minHeight: "88px",
                    border: "1px solid #575757",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    padding: "10px",
                  }}
                >
                  <InsertDriveFileOutlinedIcon />
                  <div style={{ wordBreak: "break-all" }}>{preview.originalname}</div>
                </div>
                <div className="fs-small b-mt-2" style={{ wordBreak: "break-all" }}>
                  {preview.url}
                </div>
              </div>
              <div className="col-6 b-pe-1">
                <button
                  type="button"
                  className="b-btn btn-primary w-100"
                  style={{ background: "#444", border: "none" }}
                  onClick={() => setPreview(null)}
                >
                  Back
                </button>
              </div>
              <div className="col-6 b-ps-1">
                <button
                  type="button"
                  className="b-btn btn-primary w-100"
                  onClick={() => {
                    onSelect(preview);
                  }}
                >
                  Select
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
